import { useEffect, useMemo, useRef, useState } from 'react';
import { Control, Controller, FieldValues, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { TimeField } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { RestrictedNumberField } from 'components/inputs/RestrictedNumberField';
import { useGetBlueprintDetails } from 'features/blueprints/hooks/useGetBlueprintDetails';
import { useGetBlueprintPowerProfile } from 'features/blueprints/hooks/useGetBlueprintPowerProfile';
import {
  DaysSelect,
  Modal,
  PowerProfileChart,
  PowerProfileDataPoint,
  PowerProfileMode,
  powerProfileModes,
} from 'greener-ui-library';
import { useValidatePowerProfileFile } from 'hooks/useValidatePowerProfileFile';
import { PowerProfileStatsDisplay } from 'pages/Blueprints/components/PowerProfileStatsDisplay';
import { calculateManualWeeklyStats } from 'pages/Blueprints/utils';
import { getProjectDurationWeeks } from 'pages/Blueprints/utils/getProjectDurationWeeks';
import { PowerProfileFileUpload } from '../../components/PowerProfileFileUpload';
import { BlueprintFormData } from '../types';
import { timeOptions } from '../utils/constants';
import {
  convertCsvToPowerProfileData,
  convertPowerProfileDataToCsv,
  generateSingleDayCsv,
} from '../utils/powerProfileCsvConverter';
import {
  FileProcessingResult,
  aggregateDataByMinute,
  getValidationError,
  handleFileUpload,
  handleModeChange,
  handleModeSwitchConfirm,
  updatePowerProfileCsvData,
} from '../utils/powerRequirementsUtils';
import { sortDays } from '../utils/sortDays';
import { BlueprintStepSection } from './BlueprintStepSection';
import { PowerProfileGenerationInstructions } from './PowerProfileGenerationInstructions';
import { ResponsiveFormContainer } from './ResponsiveFormContainer';

const noDataMessageMap: Record<PowerProfileMode, string> = {
  [powerProfileModes.manual]: 'blueprints:wizard.fields.powerProfile.noDataMessage.manual',
  [powerProfileModes.file]: 'blueprints:wizard.fields.powerProfile.noDataMessage.file',
};

interface PowerRequirementsStepProps {
  formMethods: UseFormReturn<BlueprintFormData>;
  blueprintId?: number;
  mode?: 'create' | 'edit';
  onPowerProfileResetChange?: (hasClickedReset: boolean) => void;
}

export const PowerRequirementsStep = ({
  formMethods,
  blueprintId,
  mode,
  onPowerProfileResetChange,
}: PowerRequirementsStepProps) => {
  const { t } = useTranslation();
  const { validateFile } = useValidatePowerProfileFile();
  const {
    control,
    formState: { errors },
  } = formMethods;

  // Fetch power profile CSV in edit mode (for both file and manual modes)
  const { powerProfileData } = useGetBlueprintPowerProfile({
    blueprintId: blueprintId?.toString() || '',
    enabled: mode === 'edit' && !!blueprintId,
  });

  const { blueprintDetails } = useGetBlueprintDetails({
    blueprintId: blueprintId?.toString() || '',
    enabled: mode === 'edit' && !!blueprintId,
  });

  const [showModeSwitchConfirmation, setShowModeSwitchConfirmation] = useState(false);
  const [pendingMode, setPendingMode] = useState<PowerProfileMode | null>(null);

  // Track if user clicked reset button in edit mode manual
  const [hasUserClickedReset, setHasUserClickedReset] = useState(false);

  // Track if any power profile input field was touched by user (only after reset in edit mode)
  const [wasPowerProfileInputTouched, setWasPowerProfileInputTouched] = useState(false);

  // Track if we should start checking touched fields (after initial render delay)
  const shouldCheckTouchedRef = useRef(false);

  // Track if we're switching modes to prevent unwanted effects
  const isSwitchingModesRef = useRef(false);

  // Track initial values after reset to detect actual changes
  const initialValuesAfterResetRef = useRef<{
    peakPowerKw: number | null | undefined;
    averagePowerKw: number | null | undefined;
    peaksPerDay: number | null | undefined;
    peaksDuration: number | null | undefined;
    restDayAveragePowerKw: number | null | undefined;
    workingDays: string[] | undefined;
    workingHoursStart: string | null | undefined;
    workingHoursEnd: string | null | undefined;
  } | null>(null);

  // State for file processing
  const [fileProcessingResult, setFileProcessingResult] = useState<FileProcessingResult | null>(null);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [originalFileName, setOriginalFileName] = useState<string | null>(null);

  // Get powerProfileMode from form instead of local state
  const powerProfileMode = formMethods.watch('powerProfileMode') as PowerProfileMode;

  // Watch all fields that affect CSV generation
  const peakPowerKw = formMethods.watch('peakPowerKw');
  const averagePowerKw = formMethods.watch('averagePowerKw');
  const peaksPerDay = formMethods.watch('peaksPerDay');
  const peaksDuration = formMethods.watch('peaksDuration');
  const restDayAveragePowerKw = formMethods.watch('restDayAveragePowerKw');
  const selectedWorkingDays = formMethods.watch('workingDays');
  const workingHoursStart = formMethods.watch('workingHoursStart');
  const workingHoursEnd = formMethods.watch('workingHoursEnd');
  const startDate = formMethods.watch('startDate');
  const endDate = formMethods.watch('endDate');
  const projectDurationWeeks = getProjectDurationWeeks({ startDate, endDate });
  const processedFileStatistics = fileProcessingResult?.processedFileStatistics;
  const backendLoadProfile = blueprintDetails?.calculations?.[0]?.loadProfile;

  // Reset hasUserClickedReset when entering edit mode manual (on mount or when mode/powerProfileMode changes)
  // This ensures chart shows API data when navigating between steps
  // Use a ref to track if we're in the process of resetting to prevent updatePowerProfileCsvData from running
  const isResettingRef = useRef(false);

  useEffect(() => {
    if (onPowerProfileResetChange) {
      onPowerProfileResetChange(hasUserClickedReset);
    }
  }, [hasUserClickedReset, onPowerProfileResetChange]);

  useEffect(() => {
    if (mode === 'edit' && powerProfileMode === powerProfileModes.manual) {
      // Don't reset if we're switching modes
      if (isSwitchingModesRef.current) {
        isSwitchingModesRef.current = false;
        return;
      }

      isResettingRef.current = true;
      setHasUserClickedReset(false);
      setWasPowerProfileInputTouched(false);
      initialValuesAfterResetRef.current = null;
      // After a brief delay, allow updates again
      setTimeout(() => {
        isResettingRef.current = false;
      }, 50);
    }
  }, [mode, powerProfileMode]);

  // Delay checking touched fields to ignore initial touched from form initialization
  useEffect(() => {
    const timer = setTimeout(() => {
      shouldCheckTouchedRef.current = true;
    }, 100); // Small delay to skip initial touched fields

    return () => clearTimeout(timer);
  }, []);

  const handleModeChangeWrapper = (newMode: PowerProfileMode) => {
    handleModeChange(
      newMode,
      powerProfileMode,
      formMethods,
      setPendingMode,
      setShowModeSwitchConfirmation,
      fileProcessingResult,
      powerProfileData || null
    );
  };

  const handleModeSwitchConfirmWrapper = () => {
    // Set flag to prevent useEffect from resetting hasUserClickedReset
    isSwitchingModesRef.current = true;

    handleModeSwitchConfirm(
      pendingMode,
      formMethods,
      setFileProcessingResult,
      setOriginalFileName,
      setShowModeSwitchConfirmation,
      setPendingMode
    );
    // Reset all chart-related state after mode switch
    setHasUserClickedReset(true);
    setWasPowerProfileInputTouched(false);
    initialValuesAfterResetRef.current = null;
    setShowModeSwitchConfirmation(false);
  };

  const handleFileUploadWrapper = async (file: File) => {
    await handleFileUpload(file, validateFile, setFileProcessingResult, setOriginalFileName, setIsProcessingFile, () =>
      updatePowerProfileCsvData(formMethods, powerProfileMode, fileProcessingResult, originalFileName, powerProfileData)
    );
  };

  // Store initial values after reset to detect actual changes
  useEffect(() => {
    if (
      mode === 'edit' &&
      powerProfileMode === powerProfileModes.manual &&
      hasUserClickedReset &&
      !initialValuesAfterResetRef.current
    ) {
      initialValuesAfterResetRef.current = {
        peakPowerKw: formMethods.getValues('peakPowerKw'),
        averagePowerKw: formMethods.getValues('averagePowerKw'),
        peaksPerDay: formMethods.getValues('peaksPerDay'),
        peaksDuration: formMethods.getValues('peaksDuration'),
        restDayAveragePowerKw: formMethods.getValues('restDayAveragePowerKw'),
        workingDays: formMethods.getValues('workingDays'),
        workingHoursStart: formMethods.getValues('workingHoursStart'),
        workingHoursEnd: formMethods.getValues('workingHoursEnd'),
      };
    }
  }, [mode, powerProfileMode, hasUserClickedReset, formMethods]);

  // Check if any power profile input field value actually changed (not just touched)
  // Only check after delay to ignore initial touched fields from form initialization
  // And only if user has clicked reset in edit mode manual
  useEffect(() => {
    if (!shouldCheckTouchedRef.current) {
      return;
    }

    if (
      mode === 'edit' &&
      powerProfileMode === powerProfileModes.manual &&
      hasUserClickedReset &&
      initialValuesAfterResetRef.current
    ) {
      const currentPeak = formMethods.getValues('peakPowerKw');
      const currentAvg = formMethods.getValues('averagePowerKw');
      const currentPeaksPerDay = formMethods.getValues('peaksPerDay');
      const currentPeaksDuration = formMethods.getValues('peaksDuration');
      const currentRestDayAveragePowerKw = formMethods.getValues('restDayAveragePowerKw');
      const currentWorkingDays = formMethods.getValues('workingDays');
      const currentWorkingHoursStart = formMethods.getValues('workingHoursStart');
      const currentWorkingHoursEnd = formMethods.getValues('workingHoursEnd');

      const initialPeak = initialValuesAfterResetRef.current.peakPowerKw;
      const initialAvg = initialValuesAfterResetRef.current.averagePowerKw;
      const initialPeaksPerDay = initialValuesAfterResetRef.current.peaksPerDay;
      const initialPeaksDuration = initialValuesAfterResetRef.current.peaksDuration;
      const initialRestDayAveragePowerKw = initialValuesAfterResetRef.current.restDayAveragePowerKw;
      const initialWorkingDays = initialValuesAfterResetRef.current.workingDays;
      const initialWorkingHoursStart = initialValuesAfterResetRef.current.workingHoursStart;
      const initialWorkingHoursEnd = initialValuesAfterResetRef.current.workingHoursEnd;

      // Check if any value actually changed (not just touched)
      const hasValueChanged =
        currentPeak !== initialPeak ||
        currentAvg !== initialAvg ||
        currentPeaksPerDay !== initialPeaksPerDay ||
        currentPeaksDuration !== initialPeaksDuration ||
        currentRestDayAveragePowerKw !== initialRestDayAveragePowerKw ||
        JSON.stringify(currentWorkingDays) !== JSON.stringify(initialWorkingDays) ||
        currentWorkingHoursStart !== initialWorkingHoursStart ||
        currentWorkingHoursEnd !== initialWorkingHoursEnd;

      if (hasValueChanged) {
        setWasPowerProfileInputTouched(true);
      }
    }
  }, [
    mode,
    powerProfileMode,
    hasUserClickedReset,
    peakPowerKw,
    averagePowerKw,
    selectedWorkingDays,
    workingHoursStart,
    workingHoursEnd,
    formMethods,
  ]);

  // Auto-update CSV data when relevant data changes
  // In edit mode manual, only update if user clicked reset AND touched any input field
  useEffect(() => {
    // Don't update if we're in the process of resetting (navigating between steps)
    if (isResettingRef.current) {
      return;
    }

    // In edit mode manual, fields don't influence chart shape - only CSV from API
    // UNLESS user clicked reset AND touched any input field
    if (
      mode === 'edit' &&
      powerProfileMode === powerProfileModes.manual &&
      (!hasUserClickedReset || !wasPowerProfileInputTouched)
    ) {
      return;
    }

    updatePowerProfileCsvData(formMethods, powerProfileMode, fileProcessingResult, originalFileName, powerProfileData);
  }, [
    mode,
    powerProfileMode,
    fileProcessingResult,
    originalFileName,
    powerProfileData,
    peakPowerKw,
    averagePowerKw,
    peaksPerDay,
    peaksDuration,
    restDayAveragePowerKw,
    selectedWorkingDays,
    workingHoursStart,
    workingHoursEnd,
    hasUserClickedReset,
    wasPowerProfileInputTouched,
    formMethods,
  ]);

  // In edit mode manual, set CSV from API to form so chart displays it
  // This ensures user sees the same chart as on details page when entering edit mode
  // Always overwrite CSV with API data if user hasn't clicked reset (when navigating between steps)
  useEffect(() => {
    if (
      mode === 'edit' &&
      powerProfileMode === powerProfileModes.manual &&
      powerProfileData &&
      powerProfileData.length > 0
    ) {
      // If user hasn't clicked reset, always set CSV from API (overwrites any existing CSV)
      // This ensures chart shows API data when navigating between steps
      if (!hasUserClickedReset) {
        const csvFromApi = convertPowerProfileDataToCsv(powerProfileData);
        formMethods.setValue('rawPowerProfileCsv', csvFromApi, { shouldValidate: false });
        formMethods.setValue('editedPowerProfileCsv', csvFromApi, { shouldValidate: false });
      }
    }
  }, [mode, powerProfileMode, powerProfileData, hasUserClickedReset, formMethods]);

  // Get chart data from CSV to avoid timezone issues with generatePowerProfileData
  // Priority:
  // - rawPowerProfileCsv (from form)
  // - powerProfileData from API (first load or after mode switch if not saved)
  const rawCsv = formMethods.watch('rawPowerProfileCsv');
  const hasCsvInForm = rawCsv && rawCsv !== '';

  const chartData = useMemo(
    () =>
      hasCsvInForm
        ? convertCsvToPowerProfileData(rawCsv)
        : mode === 'edit' && powerProfileMode === powerProfileModes.manual && hasUserClickedReset
          ? [] // Show empty chart after reset in edit mode
          : powerProfileData,
    [hasCsvInForm, rawCsv, mode, powerProfileMode, hasUserClickedReset, powerProfileData]
  );

  const chartData1minInterval = useMemo(() => (chartData ? aggregateDataByMinute(chartData) : undefined), [chartData]);

  const manualWeeklyStats = useMemo(() => {
    return calculateManualWeeklyStats({
      peakPowerKw,
      averagePowerKw,
      peaksPerDay,
      peaksDurationSeconds: peaksDuration,
      restDayAveragePowerKw,
      workingDaysCount: Array.isArray(selectedWorkingDays) ? selectedWorkingDays.length : 0,
      workingHoursStart,
      workingHoursEnd,
    });
  }, [
    powerProfileMode,
    peakPowerKw,
    averagePowerKw,
    peaksPerDay,
    peaksDuration,
    restDayAveragePowerKw,
    selectedWorkingDays,
    workingHoursStart,
    workingHoursEnd,
  ]);

  // Combine file processing errors with form validation errors
  const allValidationErrors = [
    ...(fileProcessingResult?.errors || []),
    ...(errors.rawPowerProfileCsv?.message ? [t(errors.rawPowerProfileCsv.message)] : []),
  ];

  const daysSelectOptions = [
    {
      value: 'Monday',
      label: 'main:days.monday',
    },
    {
      value: 'Tuesday',
      label: 'main:days.tuesday',
    },
    {
      value: 'Wednesday',
      label: 'main:days.wednesday',
    },
    {
      value: 'Thursday',
      label: 'main:days.thursday',
    },
    {
      value: 'Friday',
      label: 'main:days.friday',
    },
    {
      value: 'Saturday',
      label: 'main:days.saturday',
    },
    {
      value: 'Sunday',
      label: 'main:days.sunday',
    },
  ];

  const getWorkDayChartData = (): PowerProfileDataPoint[] => {
    if (
      powerProfileMode === powerProfileModes.manual &&
      peakPowerKw !== undefined &&
      peakPowerKw !== null &&
      averagePowerKw !== undefined &&
      averagePowerKw !== null &&
      workingHoursStart &&
      workingHoursEnd
    ) {
      const csv = generateSingleDayCsv(
        peakPowerKw,
        averagePowerKw,
        workingHoursStart,
        workingHoursEnd,
        formMethods.watch('peaksPerDay'),
        formMethods.watch('peaksDuration')
      );
      const allData = convertCsvToPowerProfileData(csv);

      // Filter to show only data within working hours
      const [startHour, startMinute] = workingHoursStart.split(':').map(Number);
      const [endHour, endMinute] = workingHoursEnd.split(':').map(Number);
      const startMinutes = startHour * 60 + startMinute;
      const endMinutes = endHour * 60 + endMinute;

      return allData.filter((point) => {
        const timePart = point.timestamp.split(':').slice(1).join(':');
        const [hour, minute] = timePart.split(':').map(Number);
        const pointMinutes = hour * 60 + minute;

        return pointMinutes >= startMinutes && pointMinutes <= endMinutes;
      });
    }

    return [];
  };
  const powerProfileChartData = getWorkDayChartData();

  const MAX_X_LABELS_WITHOUT_SKIPPING = 9;

  const isEditModeInReset = mode === 'edit' && powerProfileMode === powerProfileModes.manual && hasUserClickedReset;
  const isManualMode = powerProfileMode === powerProfileModes.manual;
  const canUseApiValues = !isEditModeInReset;

  const peakPower = isManualMode
    ? (peakPowerKw ?? (canUseApiValues && backendLoadProfile?.peakLoadKw))
    : (processedFileStatistics?.peakLoadKw ?? backendLoadProfile?.peakLoadKw);

  const energyRequiredWeekly = isManualMode
    ? (manualWeeklyStats?.energyRequiredWeekly ?? (canUseApiValues && backendLoadProfile?.weeklyLoadKwh))
    : (processedFileStatistics?.weeklyLoadKwh ?? backendLoadProfile?.weeklyLoadKwh);

  const averagePower = isManualMode
    ? (manualWeeklyStats?.averagePowerWeekly ?? (canUseApiValues && backendLoadProfile?.avgLoadKw))
    : (processedFileStatistics?.avgLoadKw ?? backendLoadProfile?.avgLoadKw);

  return (
    <>
      <BlueprintStepSection
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
            <Typography variant="subtitle2">{t('blueprints:wizard.fields.powerProfileGeneration.title')}</Typography>
            <PowerProfileGenerationInstructions mode={powerProfileMode} />
          </Box>
        }
        titleSpacing={20}
      >
        <PowerProfileFileUpload
          mode={powerProfileMode}
          onModeChange={handleModeChangeWrapper}
          onFileUpload={handleFileUploadWrapper}
          validationErrors={allValidationErrors}
          isProcessing={isProcessingFile}
          isSuccess={fileProcessingResult?.success || false}
        />
      </BlueprintStepSection>

      <Divider sx={{ my: 4 }} />

      {powerProfileMode === powerProfileModes.manual && (hasUserClickedReset || mode === 'create') && (
        <>
          <BlueprintStepSection title={t('blueprints:wizard.steps.workDayProfile')} bottomSpacing={12}>
            <Grid container>
              <Grid item xs={12} lg={5}>
                <ResponsiveFormContainer maxColumnWidth={400} minColumnWidth={200} gap={3}>
                  <RestrictedNumberField
                    control={control as unknown as Control<FieldValues>}
                    name="peakPowerKw"
                    label={t('blueprints:wizard.fields.peakPower')}
                    required
                    minValue={0}
                    gridSize={false}
                    useYupValidation
                    placeholder={t('blueprints:wizard.fields.powerPlaceholder')}
                  />

                  <RestrictedNumberField
                    control={control as unknown as Control<FieldValues>}
                    name="averagePowerKw"
                    label={t('blueprints:wizard.fields.averagePower')}
                    required
                    minValue={0}
                    gridSize={false}
                    useYupValidation
                    placeholder={t('blueprints:wizard.fields.powerPlaceholder')}
                  />
                </ResponsiveFormContainer>

                <Box sx={{ my: 4 }}>
                  <ResponsiveFormContainer maxColumnWidth={400} minColumnWidth={200} gap={3}>
                    <RestrictedNumberField
                      control={control as unknown as Control<FieldValues>}
                      name="peaksPerDay"
                      label={t('blueprints:wizard.fields.peaksPerDay')}
                      required
                      minValue={0}
                      gridSize={false}
                      useYupValidation
                      placeholder={t('blueprints:wizard.fields.peaksPerDayPlaceholder')}
                    />

                    <Grid item>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Controller
                          name="peaksDuration"
                          control={control}
                          rules={{ required: true }}
                          render={({ field: { onChange, ...field }, fieldState: { error } }) => (
                            <TimeField
                              data-testid="peaks-duration-field"
                              {...field}
                              size="small"
                              fullWidth
                              required
                              label={t('blueprints:wizard.fields.peaksDuration')}
                              format="mm:ss"
                              onChange={(newValue) => {
                                if (newValue) {
                                  const minutes = newValue.getMinutes();
                                  const seconds = newValue.getSeconds();
                                  onChange(minutes * 60 + seconds);
                                } else {
                                  onChange(null);
                                }
                              }}
                              slotProps={{
                                textField: {
                                  error: !!error,
                                  helperText: error ? t(getValidationError(error.message)) : '',
                                  InputLabelProps: { shrink: true },
                                },
                              }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </ResponsiveFormContainer>
                </Box>

                <Box sx={{ mt: 4, mb: { xs: 4, lg: 0 } }}>
                  <ResponsiveFormContainer maxColumnWidth={400} minColumnWidth={200} gap={3}>
                    <Autocomplete
                      data-testid="start-time"
                      freeSolo
                      size="small"
                      options={timeOptions}
                      value={formMethods.watch('workingHoursStart') || ''}
                      onChange={(_, value) => {
                        formMethods.setValue('workingHoursStart', value || '', {
                          shouldValidate: true,
                          shouldTouch: true,
                        });
                        formMethods.trigger('workingHoursEnd');
                        updatePowerProfileCsvData(
                          formMethods,
                          powerProfileMode,
                          fileProcessingResult,
                          originalFileName,
                          powerProfileData
                        );
                      }}
                      onInputChange={(_, value) => {
                        formMethods.setValue('workingHoursStart', value, { shouldValidate: true, shouldTouch: true });
                        formMethods.trigger('workingHoursEnd');
                        updatePowerProfileCsvData(
                          formMethods,
                          powerProfileMode,
                          fileProcessingResult,
                          originalFileName,
                          powerProfileData
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label={t('blueprints:wizard.fields.workingHours.start')}
                          error={!!errors.workingHoursStart}
                          helperText={
                            errors.workingHoursStart ? t(getValidationError(errors.workingHoursStart.message)) : ''
                          }
                        />
                      )}
                    />

                    <Autocomplete
                      data-testid="end-time"
                      freeSolo
                      size="small"
                      options={timeOptions}
                      value={formMethods.watch('workingHoursEnd') || ''}
                      onChange={(_, value) => {
                        formMethods.setValue('workingHoursEnd', value || '', {
                          shouldValidate: true,
                          shouldTouch: true,
                        });
                        updatePowerProfileCsvData(
                          formMethods,
                          powerProfileMode,
                          fileProcessingResult,
                          originalFileName,
                          powerProfileData
                        );
                      }}
                      onInputChange={(_, value) => {
                        formMethods.setValue('workingHoursEnd', value, { shouldValidate: true, shouldTouch: true });
                        updatePowerProfileCsvData(
                          formMethods,
                          powerProfileMode,
                          fileProcessingResult,
                          originalFileName,
                          powerProfileData
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label={t('blueprints:wizard.fields.workingHours.end')}
                          error={!!errors.workingHoursEnd}
                          helperText={
                            errors.workingHoursEnd ? t(getValidationError(errors.workingHoursEnd.message)) : ''
                          }
                        />
                      )}
                    />
                  </ResponsiveFormContainer>
                </Box>
              </Grid>

              <Grid item xs={12} lg>
                <Grid
                  item
                  sx={{
                    borderRadius: 2,
                    bgcolor: 'action.hover',
                    ml: { xs: 0, lg: 6 },
                    pt: 1.5,
                  }}
                >
                  <PowerProfileChart
                    data={powerProfileChartData}
                    title={t('blueprints:wizard.fields.powerProfile.dayProfileTitle')}
                    titleStyles={{ ml: 2, mb: -1.5 }}
                    height={150}
                    xAxisInterval={29}
                    xAxisTickFormatter={(value) => {
                      const parts = value.split(':');
                      const hourLabelsCount = Math.ceil(powerProfileChartData.length / 59);

                      const hours = parseInt(parts[1], 10);
                      const minutes = parts[2];

                      if (minutes !== '00') {
                        return '';
                      }

                      const isLabelShown = hourLabelsCount <= MAX_X_LABELS_WITHOUT_SKIPPING || hours % 2 === 0;
                      return isLabelShown ? `${parts[1]}:00` : '';
                    }}
                    orientationWarningTitle={t('blueprints:wizard.fields.powerProfile.orientationWarningTitle')}
                    orientationWarningMessage={t('blueprints:wizard.fields.powerProfile.orientationWarningMessage')}
                  />
                </Grid>
              </Grid>
            </Grid>
          </BlueprintStepSection>

          <Divider sx={{ my: 4 }} />

          <BlueprintStepSection title={`${t('blueprints:wizard.fields.workingDays.title')} *`} bottomSpacing={12}>
            <ResponsiveFormContainer maxColumnWidth={400} minColumnWidth={200} gap={3}>
              <DaysSelect
                options={daysSelectOptions}
                selectedValue={formMethods.watch('workingDays') || []}
                onChange={(value) => {
                  const newValue = Array.isArray(value) ? sortDays(value) : [];
                  formMethods.setValue('workingDays', newValue, { shouldValidate: true, shouldTouch: true });
                  updatePowerProfileCsvData(
                    formMethods,
                    powerProfileMode,
                    fileProcessingResult,
                    originalFileName,
                    powerProfileData
                  );
                }}
                error={errors.workingDays ? t('validations.required') : undefined}
              />
            </ResponsiveFormContainer>
          </BlueprintStepSection>

          <Divider sx={{ my: 4 }} />

          <BlueprintStepSection title={t('blueprints:wizard.steps.restPeriodProfile')} bottomSpacing={12}>
            <ResponsiveFormContainer maxColumnWidth={400} minColumnWidth={200} gap={3}>
              <RestrictedNumberField
                control={control as unknown as Control<FieldValues>}
                name="restDayAveragePowerKw"
                label={t('blueprints:wizard.fields.averagePower')}
                required
                minValue={0}
                gridSize={false}
                useYupValidation
                placeholder={t('blueprints:wizard.fields.powerPlaceholder')}
              />
              <span />
            </ResponsiveFormContainer>
          </BlueprintStepSection>

          <Divider sx={{ my: 4 }} />
        </>
      )}

      <PowerProfileStatsDisplay
        peakPower={peakPower}
        averagePower={averagePower}
        energyRequiredWeekly={energyRequiredWeekly}
        projectDurationWeeks={projectDurationWeeks}
      />

      <Box sx={{ mb: 0.5, bgcolor: 'action.hover', padding: '10px 16px', borderRadius: 2 }}>
        {/* Show warnings only (errors are shown in PowerProfileFileUpload) */}
        {fileProcessingResult && fileProcessingResult.warnings.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {fileProcessingResult.warnings.map((warning, index) => (
              <Typography key={index} variant="body2" color="warning.main" sx={{ mb: 1 }}>
                {warning}
              </Typography>
            ))}
          </Box>
        )}

        {powerProfileMode === powerProfileModes.manual && mode === 'edit' && !hasUserClickedReset && (
          <Alert
            severity="info"
            sx={{
              mb: 2,
              backgroundColor: (theme) => `${theme.palette.brand.main}15`,
              border: (theme) => `1px solid ${theme.palette.brand.main}40`,
              '& .MuiAlert-icon': {
                color: (theme) => theme.palette.brand.main,
              },
              '& .MuiAlert-message': {
                color: (theme) => theme.palette.text.success,
              },
            }}
            action={
              <Button
                data-testid="edit-power-profile-button"
                variant="contained"
                size="small"
                onClick={() => {
                  formMethods.setValue('averagePowerKw', undefined, { shouldValidate: false });
                  formMethods.setValue('peaksPerDay', undefined, { shouldValidate: false });
                  formMethods.setValue('peaksDuration', undefined, { shouldValidate: false });
                  formMethods.setValue('restDayAveragePowerKw', undefined, { shouldValidate: false });
                  formMethods.setValue('workingDays', [], { shouldValidate: false });
                  formMethods.setValue('rawPowerProfileCsv', '', { shouldValidate: false });
                  formMethods.setValue('editedPowerProfileCsv', '', { shouldValidate: false });
                  setHasUserClickedReset(true);
                  setWasPowerProfileInputTouched(false);
                  initialValuesAfterResetRef.current = null;
                }}
                sx={{
                  alignSelf: 'center',
                }}
              >
                {t('blueprints:wizard.fields.powerProfile.resetButton')}
              </Button>
            }
          >
            <Box>{t('blueprints:wizard.fields.powerProfile.editModeDefaultWarning')}</Box>
          </Alert>
        )}

        <PowerProfileChart
          data={chartData1minInterval}
          title={t('blueprints:wizard.fields.powerProfile.title')}
          peakPowerKw={
            // In edit mode manual, pass props only if user clicked reset AND touched any input field
            // In create mode, always pass props to generate chart from inputs
            powerProfileMode === powerProfileModes.manual &&
            (mode === 'create' || (mode === 'edit' && hasUserClickedReset && wasPowerProfileInputTouched)) &&
            !errors.peakPowerKw &&
            !errors.averagePowerKw
              ? formMethods.watch('peakPowerKw')
              : undefined
          }
          averagePowerKw={
            powerProfileMode === powerProfileModes.manual &&
            (mode === 'create' || (mode === 'edit' && hasUserClickedReset && wasPowerProfileInputTouched)) &&
            !errors.peakPowerKw &&
            !errors.averagePowerKw
              ? formMethods.watch('averagePowerKw')
              : undefined
          }
          workingDays={
            powerProfileMode === powerProfileModes.manual &&
            (mode === 'create' || (mode === 'edit' && hasUserClickedReset && wasPowerProfileInputTouched))
              ? formMethods.watch('workingDays')
              : undefined
          }
          workingHoursStart={
            powerProfileMode === powerProfileModes.manual &&
            (mode === 'create' || (mode === 'edit' && hasUserClickedReset && wasPowerProfileInputTouched))
              ? formMethods.watch('workingHoursStart')
              : undefined
          }
          workingHoursEnd={
            powerProfileMode === powerProfileModes.manual &&
            (mode === 'create' || (mode === 'edit' && hasUserClickedReset && wasPowerProfileInputTouched))
              ? formMethods.watch('workingHoursEnd')
              : undefined
          }
          height={250}
          noDataMessage={t(noDataMessageMap[powerProfileMode])}
          orientationWarningTitle={t('blueprints:wizard.fields.powerProfile.orientationWarningTitle')}
          orientationWarningMessage={t('blueprints:wizard.fields.powerProfile.orientationWarningMessage')}
        />
      </Box>
      <Typography variant="caption">{t('blueprints:details.powerProfile.disclaimer')}</Typography>

      <Divider sx={{ my: 3 }} />

      <BlueprintStepSection title={t('blueprints:wizard.fields.mainsConnection.title')} titleSpacing={20}>
        <ResponsiveFormContainer maxColumnWidth={250} minColumnWidth={200}>
          <Box>
            <RadioGroup
              row
              value={formMethods.watch('mainsConnection')}
              onChange={(e) => {
                const value = e.target.value === 'true';
                formMethods.setValue('mainsConnection', value, { shouldValidate: true });
                if (value) {
                  formMethods.setValue('mainsConnectionCapacityUnit', 'amps', { shouldValidate: true });
                }
              }}
            >
              <FormControlLabel
                value={true}
                control={<Radio />}
                label={t('blueprints:wizard.fields.mainsConnection.yes')}
              />
              <FormControlLabel
                value={false}
                control={<Radio />}
                label={t('blueprints:wizard.fields.mainsConnection.no')}
              />
            </RadioGroup>
            {errors.mainsConnection && (
              <Typography color="error" sx={{ fontSize: 12, mt: 1 }}>
                {t('validations.required')}
              </Typography>
            )}
          </Box>
        </ResponsiveFormContainer>
      </BlueprintStepSection>

      {formMethods.watch('mainsConnection') === true && (
        <BlueprintStepSection title={t('blueprints:wizard.fields.mainConnectionCapacity.title')}>
          <ResponsiveFormContainer maxColumnWidth={516} minColumnWidth={200}>
            <Box mb={2}>
              <RadioGroup
                row
                value={formMethods.watch('mainsConnectionCapacityUnit')}
                onChange={(e) => {
                  const value = e.target.value;
                  formMethods.setValue('mainsConnectionCapacityUnit', value, { shouldValidate: true });
                  if (value === 'amps') {
                    formMethods.setValue('maxKwAvailable', undefined);
                  }
                  if (value === 'kw') {
                    formMethods.setValue('maxAmpsAvailable', undefined);
                  }
                }}
              >
                <FormControlLabel
                  value="amps"
                  control={<Radio />}
                  label={t('blueprints:wizard.fields.mainConnectionCapacity.byMaxCurrent')}
                />
                <FormControlLabel
                  value="kw"
                  control={<Radio />}
                  label={t('blueprints:wizard.fields.mainConnectionCapacity.byMaxPower')}
                />
              </RadioGroup>
              {errors.mainsConnectionCapacityUnit && (
                <Typography color="error" sx={{ fontSize: 12, mt: 1 }}>
                  {t('validations.required')}
                </Typography>
              )}
            </Box>
          </ResponsiveFormContainer>

          {formMethods.watch('mainsConnectionCapacityUnit') === 'amps' && (
            <ResponsiveFormContainer maxColumnWidth={400} minColumnWidth={200}>
              <RestrictedNumberField
                control={control as unknown as Control<FieldValues>}
                name="maxAmpsAvailable"
                label={t('blueprints:wizard.fields.mainConnectionCapacity.maxCurrent')}
                required
                minValue={0}
                useYupValidation
                placeholder={t('blueprints:wizard.fields.mainConnectionCapacity.capacityPlaceholder')}
              />
              <span />
            </ResponsiveFormContainer>
          )}

          {formMethods.watch('mainsConnectionCapacityUnit') === 'kw' && (
            <ResponsiveFormContainer maxColumnWidth={400} minColumnWidth={200}>
              <RestrictedNumberField
                control={control as unknown as Control<FieldValues>}
                name="maxKwAvailable"
                label={t('blueprints:wizard.fields.mainConnectionCapacity.maxPower')}
                required
                minValue={0}
                useYupValidation
                placeholder={t('blueprints:wizard.fields.mainConnectionCapacity.capacityPlaceholder')}
              />
              <span />
            </ResponsiveFormContainer>
          )}
        </BlueprintStepSection>
      )}

      <Divider sx={{ my: 4 }} />

      <Box mb={4}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formMethods.watch('kitePowerScenario') !== null}
              onChange={(e) => {
                formMethods.setValue('kitePowerScenario', e.target.checked ? 'low_wind' : null, {
                  shouldValidate: true,
                });
              }}
            />
          }
          label={t('blueprints:wizard.fields.kitePower.title')}
        />
      </Box>

      {formMethods.watch('kitePowerScenario') !== null && (
        <Box mb={2}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            {t('blueprints:wizard.fields.kitePower.selectWindScenario')}
          </Typography>
          <RadioGroup
            value={formMethods.watch('kitePowerScenario')}
            onChange={(e) => {
              formMethods.setValue('kitePowerScenario', e.target.value as 'low_wind' | 'medium_wind' | 'high_wind', {
                shouldValidate: true,
              });
            }}
          >
            <Box mb={3}>
              <FormControlLabel
                value="low_wind"
                control={<Radio />}
                label={
                  <>
                    <Typography fontWeight={600}>
                      {t('blueprints:wizard.fields.kitePower.windScenarios.low.title')}
                    </Typography>
                    <Typography>{t('blueprints:wizard.fields.kitePower.windScenarios.low.details')}</Typography>
                  </>
                }
              />
            </Box>
            <Box mb={3}>
              <FormControlLabel
                value="medium_wind"
                control={<Radio />}
                label={
                  <>
                    <Typography fontWeight={600}>
                      {t('blueprints:wizard.fields.kitePower.windScenarios.medium.title')}
                    </Typography>
                    <Typography>{t('blueprints:wizard.fields.kitePower.windScenarios.medium.details')}</Typography>
                  </>
                }
              />
            </Box>
            <Box>
              <FormControlLabel
                value="high_wind"
                control={<Radio />}
                label={
                  <>
                    <Typography fontWeight={600}>
                      {t('blueprints:wizard.fields.kitePower.windScenarios.high.title')}
                    </Typography>
                    <Typography>{t('blueprints:wizard.fields.kitePower.windScenarios.high.details')}</Typography>
                  </>
                }
              />
            </Box>
          </RadioGroup>
        </Box>
      )}

      <Modal
        title={t('blueprints:wizard.fields.powerProfileGeneration.modeSwitchConfirmation.title')}
        isOpen={showModeSwitchConfirmation}
        onClose={() => setShowModeSwitchConfirmation(false)}
        onCancel={() => setShowModeSwitchConfirmation(false)}
        onPrimaryButtonClick={handleModeSwitchConfirmWrapper}
        primaryButtonTitle={t('blueprints:wizard.fields.powerProfileGeneration.modeSwitchConfirmation.confirm')}
      >
        {t('blueprints:wizard.fields.powerProfileGeneration.modeSwitchConfirmation.message')}
      </Modal>
    </>
  );
};
